import React, { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Chip,
  Rating,
  Grid,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogContent,
  IconButton,
} from "@mui/material"
import {
  ArrowBack,
  LocationOn,
  Accessible,
  Wc,
  DirectionsWalk,
  ChildCare,
  HealthAndSafety,
  Pets,
  Vaccines,
  Close,
  ChevronLeft,
  ChevronRight,
} from "@mui/icons-material"
import { fetchBathroomById, fetchBathroomReviews } from "../services/bathroomService"
import { formatDistance } from "../utils/distanceUtils"

// Convert camelCase to Title Case
const camelToTitle = str => {
  return str
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, str => str.toUpperCase())
    .trim()
}

// Map amenity keys to icons
const getAmenityIcon = amenityKey => {
  const iconMap = {
    babyChangingTable: ChildCare,
    nursingRoom: HealthAndSafety,
    petRelief: Pets,
    sharpsReceptacle: Vaccines,
  }
  return iconMap[amenityKey] || null
}

const BathroomDetailsPage = () => {
  const params = useParams()
  const navigate = useNavigate()
  const [bathroom, setBathroom] = useState(null)
  const [reviews, setReviews] = useState([])
  const [loading, setLoading] = useState(true)
  const [reviewsLoading, setReviewsLoading] = useState(true)
  const [imageViewerOpen, setImageViewerOpen] = useState(false)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [viewerImages, setViewerImages] = useState([])

  // Reconstruct the full ID from params (handles "locationId/bathroomId" format)
  const id = params.id + (params["*"] ? `/${params["*"]}` : "")

  const handleImageClick = (images, index) => {
    setViewerImages(images)
    setCurrentImageIndex(index)
    setImageViewerOpen(true)
  }

  const handleNextImage = () => {
    setCurrentImageIndex(prev => (prev + 1) % viewerImages.length)
  }

  const handlePrevImage = () => {
    setCurrentImageIndex(prev => (prev - 1 + viewerImages.length) % viewerImages.length)
  }

  useEffect(() => {
    const fetchBathroom = async () => {
      try {
        setLoading(true)
        const data = await fetchBathroomById(id)
        setBathroom(data)
      } catch (error) {
        console.error("Error fetching bathroom:", error)
      } finally {
        setLoading(false)
      }
    }

    const fetchReviews = async () => {
      try {
        setReviewsLoading(true)
        const reviewsData = await fetchBathroomReviews(id)
        setReviews(reviewsData)
      } catch (error) {
        console.error("Error fetching reviews:", error)
      } finally {
        setReviewsLoading(false)
      }
    }

    fetchBathroom()
    fetchReviews()
  }, [id])

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Container>
    )
  }

  if (!bathroom) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper elevation={2} sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h5" gutterBottom>
            Bathroom Not Found
          </Typography>
          <Button startIcon={<ArrowBack />} onClick={() => navigate("/")} sx={{ mt: 2 }}>
            Back to Home
          </Button>
        </Paper>
      </Container>
    )
  }

  const getGenderIcon = gender => {
    switch (gender) {
      case "men":
        return "♂️"
      case "women":
        return "♀️"
      case "unisex":
        return "⚧️"
      default:
        return "🚻"
    }
  }

  const getGenderColor = gender => {
    switch (gender) {
      case "men":
        return "primary"
      case "women":
        return "secondary"
      case "unisex":
        return "success"
      default:
        return "default"
    }
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Back Button */}
      <Button startIcon={<ArrowBack />} onClick={() => navigate("/")} sx={{ mb: 2 }}>
        Back to List
      </Button>

      <Paper elevation={2} sx={{ overflow: "hidden" }}>
        {/* Photos */}
        {bathroom.photos && bathroom.photos.length > 0 && (
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: 300,
              bgcolor: "grey.200",
              cursor: "pointer",
              "&:hover": { opacity: 0.9 },
            }}
            onClick={() => handleImageClick(bathroom.photos, 0)}
          >
            <img
              src={bathroom.photos[0]}
              alt={bathroom.name || "Bathroom"}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
              onError={e => {
                e.target.style.display = "none"
              }}
            />
            {bathroom.photos.length > 1 && (
              <Chip
                label={`${bathroom.photos.length} photos`}
                size="small"
                sx={{
                  position: "absolute",
                  bottom: 16,
                  right: 16,
                  bgcolor: "rgba(0,0,0,0.6)",
                  color: "white",
                }}
              />
            )}
          </Box>
        )}

        <Box sx={{ p: 3 }}>
          {/* Title and Rating */}
          <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
            {bathroom.name || "Unnamed Location"}
          </Typography>

          {bathroom.ratings?.overall && (
            <Box display="flex" alignItems="center" mb={2}>
              <Rating value={bathroom.ratings.overall} precision={0.1} size="large" readOnly />
              <Typography variant="h6" sx={{ ml: 1 }}>
                {bathroom.ratings.overall.toFixed(1)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                ({bathroom.reviewCount || 0} reviews)
              </Typography>
            </Box>
          )}

          {/* Address */}
          {bathroom.address && (
            <Box display="flex" alignItems="flex-start" mb={2}>
              <LocationOn color="action" sx={{ mr: 1, mt: 0.5 }} />
              <Typography variant="body1" color="text.secondary">
                {bathroom.address}
              </Typography>
            </Box>
          )}

          {/* Distance */}
          {bathroom.distance !== undefined && (
            <Box display="flex" alignItems="center" mb={2}>
              <DirectionsWalk color="action" sx={{ mr: 1 }} />
              <Typography variant="body1" color="text.secondary">
                {formatDistance(bathroom.distance)} away
              </Typography>
            </Box>
          )}

          <Divider sx={{ my: 3 }} />

          {/* Key Information */}
          <Typography variant="h6" gutterBottom fontWeight="bold">
            Information
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <Card variant="outlined">
                <CardContent>
                  <Box display="flex" alignItems="center" mb={1}>
                    <Wc color="primary" sx={{ mr: 1 }} />
                    <Typography variant="subtitle2" color="text.secondary">
                      Gender Type
                    </Typography>
                  </Box>
                  <Chip
                    label={`${getGenderIcon(bathroom.genderType)} ${bathroom.genderType || "Unisex"}`}
                    color={getGenderColor(bathroom.genderType)}
                    size="small"
                  />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Card variant="outlined">
                <CardContent>
                  <Box display="flex" alignItems="center" mb={1}>
                    <Accessible color="primary" sx={{ mr: 1 }} />
                    <Typography variant="subtitle2" color="text.secondary">
                      Accessibility
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center">
                    <Rating value={bathroom.ratings?.accessibility || 0} max={5} size="small" readOnly />
                    <Typography variant="body2" sx={{ ml: 1 }}>
                      {bathroom.ratings?.accessibility ? `${bathroom.ratings.accessibility}/5` : "Not rated"}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Amenities */}
          {bathroom.amenities && Object.entries(bathroom.amenities).filter(([, value]) => value === true).length > 0 && (
            <>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Amenities
              </Typography>
              <List sx={{ mb: 3 }}>
                {Object.entries(bathroom.amenities)
                  .filter(([, value]) => value === true)
                  .map(([amenity], index) => {
                    const IconComponent = getAmenityIcon(amenity)
                    return (
                      <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 40 }}>{IconComponent && <IconComponent color="primary" />}</ListItemIcon>
                        <ListItemText primary={camelToTitle(amenity)} />
                      </ListItem>
                    )
                  })}
              </List>
            </>
          )}

          {/* Description */}
          {bathroom.description && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Description
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                {bathroom.description}
              </Typography>
            </>
          )}

          {/* Additional Photos */}
          {bathroom.photos && bathroom.photos.length > 1 && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Photos ({bathroom.photos.length})
              </Typography>
              <Grid container spacing={2}>
                {bathroom.photos.map((photo, index) => (
                  <Grid item xs={6} sm={4} key={index}>
                    <Box
                      sx={{
                        width: "100%",
                        paddingTop: "100%",
                        position: "relative",
                        borderRadius: 1,
                        overflow: "hidden",
                        bgcolor: "grey.200",
                        cursor: "pointer",
                        "&:hover": { opacity: 0.8 },
                      }}
                      onClick={() => handleImageClick(bathroom.photos, index)}
                    >
                      <img
                        src={photo}
                        alt={`${bathroom.name} - ${index + 1}`}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                        onError={e => {
                          e.target.style.display = "none"
                        }}
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </>
          )}

          {/* Reviews Section */}
          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" gutterBottom fontWeight="bold">
            Reviews {reviews.length > 0 && `(${reviews.length})`}
          </Typography>

          {reviewsLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : reviews.length > 0 ? (
            <Box sx={{ mt: 2 }}>
              {reviews.map((review, index) => (
                <Card key={review.id} variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    {/* Review Header */}
                    <Box display="flex" alignItems="center" mb={2}>
                      <Avatar
                        sx={{
                          bgcolor: "primary.main",
                          width: 40,
                          height: 40,
                          mr: 2,
                        }}
                      >
                        {review.userName ? review.userName.charAt(0).toUpperCase() : "?"}
                      </Avatar>
                      <Box flexGrow={1}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {review.userName || "Anonymous"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {review.timestamp
                            ? new Date(review.timestamp).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                              })
                            : "Date unknown"}
                        </Typography>
                      </Box>
                      {review.rating && <Rating value={review.rating} precision={0.5} size="small" readOnly />}
                    </Box>

                    {/* Review Comment */}
                    {review.comment && (
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {review.comment}
                      </Typography>
                    )}

                    {/* Review Photos */}
                    {review.photos && review.photos.length > 0 && (
                      <Grid container spacing={1} sx={{ mt: 1 }}>
                        {review.photos.map((photo, photoIndex) => (
                          <Grid item xs={4} sm={3} key={photoIndex}>
                            <Box
                              sx={{
                                width: "100%",
                                paddingTop: "100%",
                                position: "relative",
                                borderRadius: 1,
                                overflow: "hidden",
                                bgcolor: "grey.200",
                              }}
                            >
                              <img
                                src={photo}
                                alt={`Review ${index + 1} attachment ${photoIndex + 1}`}
                                style={{
                                  position: "absolute",
                                  top: 0,
                                  left: 0,
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                                onError={e => {
                                  e.target.style.display = "none"
                                }}
                              />
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              No reviews yet. Be the first to review this bathroom on the mobile app!
            </Typography>
          )}
        </Box>
      </Paper>

      {/* Image Viewer Dialog */}
      <Dialog
        open={imageViewerOpen}
        onClose={() => setImageViewerOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "black",
            boxShadow: 24,
          },
        }}
      >
        <IconButton
          onClick={() => setImageViewerOpen(false)}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "white",
            bgcolor: "rgba(0,0,0,0.5)",
            "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
            zIndex: 1,
          }}
        >
          <Close />
        </IconButton>

        <DialogContent sx={{ p: 0, position: "relative", bgcolor: "black" }}>
          {viewerImages.length > 0 && (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "70vh",
                  position: "relative",
                }}
              >
                <img
                  src={viewerImages[currentImageIndex]}
                  alt={`${currentImageIndex + 1} of ${viewerImages.length}`}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "80vh",
                    objectFit: "contain",
                  }}
                />
              </Box>

              {viewerImages.length > 1 && (
                <>
                  <IconButton
                    onClick={handlePrevImage}
                    sx={{
                      position: "absolute",
                      left: 16,
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "white",
                      bgcolor: "rgba(0,0,0,0.5)",
                      "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                    }}
                  >
                    <ChevronLeft fontSize="large" />
                  </IconButton>

                  <IconButton
                    onClick={handleNextImage}
                    sx={{
                      position: "absolute",
                      right: 16,
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "white",
                      bgcolor: "rgba(0,0,0,0.5)",
                      "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                    }}
                  >
                    <ChevronRight fontSize="large" />
                  </IconButton>

                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 16,
                      left: "50%",
                      transform: "translateX(-50%)",
                      color: "white",
                      bgcolor: "rgba(0,0,0,0.6)",
                      px: 2,
                      py: 1,
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="body2">
                      {currentImageIndex + 1} / {viewerImages.length}
                    </Typography>
                  </Box>
                </>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </Container>
  )
}

export default BathroomDetailsPage
